import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { GameState } from '../../types';
import { calculateScore, calculatePowerUpScore, formatAnswer, getAccuracyMessage, getMeasurementTypeDisplay, generateQuestionForGameMode } from '../../utils/gameLogic';
import { PowerUpsPanel } from './PowerUpsPanel';
import { Timer } from './Timer';
import { usePowerUp } from '../../data/powerUps';
import audioFileService from '../../services/audioFileService';

interface GameScreenProps {
  gameState: GameState;
  onNextQuestion: () => void;
  onUpdateScore: (points: number, isCorrect: boolean) => void;
  onEndGame: () => void;
  onEndTimedGame?: () => void | Promise<void>;
  onUpdateGameState?: (updates: Partial<GameState>) => void;
}

const GameScreen: React.FC<GameScreenProps> = ({
  gameState,
  onNextQuestion,
  onUpdateScore,
  onEndGame,
  onEndTimedGame,
  onUpdateGameState
}) => {
  const [userGuess, setUserGuess] = useState<string>('');
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState<{
    points: number;
    accuracy: number;
    isCorrect: boolean;
    usedOrderOfMagnitude?: boolean;
    usedDoublePoints?: boolean;
    newLives?: number;
  } | null>(null);
  const [showClue, setShowClue] = useState(false);
  const [currentLives, setCurrentLives] = useState<number>(gameState.lives || 5);

  const isPowerUpsMode = gameState.gameMode === 'power_ups';
  const isTimedMode = gameState.gameMode === 'timed';

  // Sync currentLives with gameState.lives when it changes
  useEffect(() => {
    if (gameState.lives !== undefined) {
      setCurrentLives(gameState.lives);
    }
  }, [gameState.lives]);

  // Timer handlers for timed mode
  const handleTimeUp = () => {
    if (isTimedMode && gameState.isGameActive) {
      if (onEndTimedGame) {
        onEndTimedGame();
      } else {
        onEndGame();
      }
    }
  };

  const handleTimerTick = (timeLeft: number) => {
    if (onUpdateGameState) {
      onUpdateGameState({ timeRemaining: timeLeft });
    }
  };

  if (!gameState.currentQuestion || !gameState.isGameActive) {
    return (
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-8">Game Over!</h2>
        <div className="card">
          <h3 className="text-2xl font-bold text-banana-800 mb-4">Final Score: {gameState.score}</h3>
          <p className="text-lg mb-4">Questions Answered: {gameState.questionsAnswered}</p>
          <p className="text-lg mb-6">Best Streak: {gameState.streak}</p>
          <div className="flex gap-4 justify-center">
            <button 
              className="btn-primary"
              onClick={onEndGame}
            >
              🔄 Play Again
            </button>
            <button 
              className="btn-secondary"
              onClick={onEndGame}
            >
              🏠 Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { currentQuestion } = gameState;
  const measurementDisplay = getMeasurementTypeDisplay(currentQuestion.measurementType);

  // Handle power-up usage
  const handleUsePowerUp = (powerUpId: string) => {
    if (!gameState.powerUps || !onUpdateGameState) return;

    switch (powerUpId) {
      case 'change_object':
        // Generate a new question with easier objects
        const newQuestion = generateQuestionForGameMode(
          gameState.gameMode,
          'easy', // Force easy difficulty
          gameState.currentMeasurementType
        );
        onUpdateGameState({
          currentQuestion: newQuestion,
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        break;

      case 'change_unit':
        // Generate a new question with different measurement type
        const measurementTypes: ('length' | 'weight' | 'volume')[] = ['length', 'weight', 'volume'];
        const currentType = gameState.currentQuestion?.measurementType;
        const availableTypes = measurementTypes.filter(type => type !== currentType);
        const newType = availableTypes[Math.floor(Math.random() * availableTypes.length)];
        
        const newQuestionWithType = generateQuestionForGameMode(
          gameState.gameMode,
          gameState.difficulty,
          newType
        );
        onUpdateGameState({
          currentQuestion: newQuestionWithType,
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        break;

      case 'double_points':
        onUpdateGameState({
          activePowerUps: { ...gameState.activePowerUps, doublePoints: true },
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        break;

      case 'order_of_magnitude':
        onUpdateGameState({
          activePowerUps: { ...gameState.activePowerUps, orderOfMagnitude: true },
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        break;

      case 'clue':
        setShowClue(true);
        audioFileService.play('hint');
        onUpdateGameState({
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        break;

      case 'skip':
        onUpdateGameState({
          powerUps: usePowerUp(gameState.powerUps, powerUpId)
        });
        handleNextQuestion();
        break;
    }
  };

  // Generate clue for the current question
  const generateClue = () => {
    if (!gameState.currentQuestion) return '';
    
    const { targetObject, referenceObject, measurementType } = gameState.currentQuestion;
    const otherTypes = ['length', 'weight', 'volume'].filter(type => type !== measurementType) as ('length' | 'weight' | 'volume')[];
    const clueType = otherTypes[Math.floor(Math.random() * otherTypes.length)];
    
    const targetValue = targetObject.measurements[clueType];
    const referenceValue = referenceObject.measurements[clueType];
    const ratio = targetValue / referenceValue;
    
    const typeNames = {
      length: 'length',
      weight: 'weight',
      volume: 'volume'
    };
    
    return `💡 Clue: In terms of ${typeNames[clueType]}, the ratio is approximately ${formatAnswer(ratio)}`;
  };

  const handleSubmitGuess = async () => {
    const guess = parseFloat(userGuess);
    if (isNaN(guess) || guess <= 0) {
      alert('Please enter a valid positive number!');
      return;
    }

    // Play click sound and prepare audio for subsequent sounds
    audioFileService.play('click');
    audioFileService.prepareAudio(); // Ensure audio context is ready

    let result;
    if (isPowerUpsMode && gameState.activePowerUps) {
      // Use power-up scoring
      result = calculatePowerUpScore(guess, currentQuestion.correctAnswer, gameState.activePowerUps);
      
      // Clear active power-ups after use
      if (onUpdateGameState) {
        onUpdateGameState({
          activePowerUps: {}
        });
      }
    } else {
      // Use regular scoring
      const regularResult = calculateScore(guess, currentQuestion.correctAnswer);
      result = {
        ...regularResult,
        usedOrderOfMagnitude: false,
        usedDoublePoints: false
      };
    }

    // Play correct/incorrect sound based on result
    if (result.isCorrect) {
      // Play perfect sound for 95%+ accuracy, otherwise correct sound
      if (result.accuracy >= 95) {
        audioFileService.play('perfect');
      } else {
        audioFileService.play('correct');
      }
    } else if (result.points === 0) {
      // Only play incorrect sound for 0 points (< 25% accuracy)
      audioFileService.play('incorrect');
    }

    setLastResult(result);
    setShowResult(true);

    // Handle lives in power-ups mode
    let newLives = currentLives;
    if (isPowerUpsMode && !result.isCorrect && onUpdateGameState) {
      newLives = currentLives - 1;
      setCurrentLives(newLives); // Update local state immediately
      onUpdateGameState({ lives: newLives }); // Update parent state
      
      // Update the result to include the correct lives count
      setLastResult({
        ...result,
        newLives // Add this for display purposes
      });
      
      if (newLives <= 0) {
        // Game over
        setTimeout(() => {
          onEndGame();
        }, 2000); // Give time to see the result
        return;
      }
    }

    onUpdateScore(result.points, result.isCorrect);
  };

  const handleNextQuestion = () => {
    // Play click sound
    audioFileService.play('click');
    
    setUserGuess('');
    setShowResult(false);
    setLastResult(null);
    setShowClue(false); // Reset clue state
    
    onNextQuestion();
  };

  const handleEndGame = () => {
    // Play click sound
    audioFileService.play('click');
    onEndGame();
  };

  const getQuestionText = () => {
    const { targetObject, referenceObject, measurementType } = currentQuestion;
    
    switch (measurementType) {
      case 'length':
        return `How many ${targetObject.displayName.toLowerCase()}s long is a ${referenceObject.displayName.toLowerCase()}?`;
      case 'volume':
        return `How many ${targetObject.displayName.toLowerCase()}s fit in a ${referenceObject.displayName.toLowerCase()}?`;
      case 'weight':
        return `How many ${targetObject.displayName.toLowerCase()}s weigh as much as a ${referenceObject.displayName.toLowerCase()}?`;
      default:
        return `How many ${targetObject.displayName.toLowerCase()}s equal a ${referenceObject.displayName.toLowerCase()}?`;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Progress and Exit Button */}
      <div className="flex justify-between items-center mb-6">
        <div className="score-display text-sm sm:text-base font-medium">
          Score: {gameState.score}
        </div>
        
        {/* Center section: Timer for timed mode, Question count for others */}
        <div className="text-base sm:text-lg font-semibold text-banana-800 text-center">
          {isTimedMode && gameState.timeRemaining !== undefined ? (
            <Timer 
              timeRemaining={gameState.timeRemaining} 
              onTimeUp={handleTimeUp}
              onTick={handleTimerTick}
            />
          ) : (
            `Q ${gameState.questionsAnswered}/${gameState.totalQuestions === Infinity ? '∞' : gameState.totalQuestions}`
          )}
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="score-display text-sm sm:text-base font-medium">
            Streak: {gameState.streak}
          </div>
          <button
            onClick={handleEndGame}
            className="btn-secondary px-2 sm:px-3 py-1.5 sm:py-2 text-sm sm:text-base font-medium"
            title="Exit to Home"
          >
            <span className="sm:hidden">🏠</span>
            <span className="hidden sm:inline">🏠 Exit</span>
          </button>
        </div>
      </div>

      {/* Measurement Type Indicator */}
      <div className="text-center mb-4 sm:mb-6">
        <div className="inline-flex items-center gap-1 sm:gap-2 bg-white rounded-full px-3 sm:px-4 py-1 sm:py-2 shadow-md">
          <span className="text-lg sm:text-2xl">{measurementDisplay.icon}</span>
          <span className="font-semibold text-banana-800 text-sm sm:text-base">
            <span className="hidden sm:inline">{measurementDisplay.name} Comparison</span>
            <span className="sm:hidden">{measurementDisplay.name}</span>
          </span>
          {currentQuestion.measurementType === 'length' && (
            <div className="relative group">
              <span className="text-banana-600 cursor-help text-sm">ℹ️</span>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Always uses the longest dimension of each object
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
          {currentQuestion.measurementType === 'volume' && (
            <div className="relative group">
              <span className="text-banana-600 cursor-help text-sm">ℹ️</span>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                Based on liquid volume - like filling containers with water
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Power-ups Panel (only in power-ups mode) */}
      {isPowerUpsMode && gameState.powerUps && currentLives !== undefined && (
        <PowerUpsPanel
          powerUps={gameState.powerUps}
          lives={currentLives}
          onUsePowerUp={handleUsePowerUp}
          disabled={showResult}
        />
      )}

      {/* Clue Display */}
      {showClue && (
        <motion.div 
          className="card bg-blue-50 mb-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-blue-800 text-center">{generateClue()}</p>
        </motion.div>
      )}

      {/* Question Card */}
      <motion.div 
        className="card mb-8"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-xl md:text-3xl font-bold text-center text-banana-800 mb-2 md:mb-4">
          {getQuestionText()}
        </h2>
        {currentQuestion.measurementType === 'length' && (
          <p className="text-sm text-banana-600 text-center mb-4 md:mb-6">
            (using the longest dimension)
          </p>
        )}

        {/* Objects Display - Target object (smaller) on left, Reference object (larger) on right */}
        <div className="grid grid-cols-2 gap-3 md:gap-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="text-3xl md:text-6xl mb-1 md:mb-4">{currentQuestion.targetObject.emoji}</div>
            <h3 className="text-base md:text-xl font-bold text-banana-800 mb-1 md:mb-2">
              {currentQuestion.targetObject.displayName}
            </h3>
            <p className="text-xs md:text-base text-banana-600 mb-2 leading-tight">
              {currentQuestion.targetObject.description}
            </p>
          </div>
          
          <div className="text-center">
            <div className="text-3xl md:text-6xl mb-1 md:mb-4">{currentQuestion.referenceObject.emoji}</div>
            <h3 className="text-base md:text-xl font-bold text-banana-800 mb-1 md:mb-2">
              {currentQuestion.referenceObject.displayName}
            </h3>
            <p className="text-xs md:text-base text-banana-600 mb-2 leading-tight">
              {currentQuestion.referenceObject.description}
            </p>
          </div>
        </div>

        {/* Input/Results Section with Inline Layout */}
        <div className="text-center">
          {/* Input and Button Section - Always visible with consistent layout */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-3">
              <input
                type="number"
                inputMode="decimal"
                value={userGuess}
                onChange={(e) => setUserGuess(e.target.value)}
                placeholder="Enter your guess..."
                className={`px-3 sm:px-4 py-2 sm:py-3 text-lg sm:text-xl border-2 rounded-lg focus:outline-none w-40 sm:w-48 ${
                  showResult 
                    ? 'border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed' 
                    : 'border-banana-300 focus:border-banana-500'
                }`}
                min="0"
                step="0.1"
                disabled={showResult}
              />
              {!showResult ? (
                <button
                  onClick={handleSubmitGuess}
                  className="btn-primary px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
                  disabled={!userGuess}
                >
                  <span className="sm:hidden">Submit</span>
                  <span className="hidden sm:inline">Submit Guess</span>
                </button>
              ) : (
                <button
                  onClick={handleNextQuestion}
                  className="btn-primary px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base"
                >
                  {gameState.questionsAnswered >= gameState.totalQuestions ? 'Finish' : 'Next'}
                </button>
              )}
            </div>
          </div>
          
          {/* Results Section - Appears BELOW the button */}
          {showResult && (
            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h3 className="text-lg md:text-2xl font-bold">
                {lastResult && getAccuracyMessage(lastResult.accuracy)}
              </h3>
              
              {/* Results Cards - Horizontal on larger screens */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 max-w-2xl mx-auto">
                <div className="card bg-banana-50 py-2 sm:py-3">
                  <p className="text-xs text-banana-600">Your Guess</p>
                  <p className="text-base sm:text-lg font-bold text-banana-800">{userGuess}</p>
                </div>
                <div className="card bg-green-50 py-2 sm:py-3">
                  <p className="text-xs text-green-600">Correct Answer</p>
                  <p className="text-base sm:text-lg font-bold text-green-800">
                    {formatAnswer(currentQuestion.correctAnswer)}
                  </p>
                </div>
                <div className="card bg-blue-50 py-2 sm:py-3">
                  <p className="text-xs text-blue-600">Accuracy</p>
                  <p className="text-base sm:text-lg font-bold text-blue-800">
                    {lastResult?.accuracy}%
                  </p>
                </div>
              </div>
              
              {/* Points and Status */}
              <div className="text-center">
                <p className="text-sm sm:text-base text-banana-700">
                  You earned <span className="font-bold text-banana-800">{lastResult?.points} points!</span>
                </p>
                <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mt-2">
                  {lastResult?.usedDoublePoints && (
                    <span className="text-xs text-purple-600 font-medium bg-purple-100 px-2 py-1 rounded">🎯 Double Points</span>
                  )}
                  {lastResult?.usedOrderOfMagnitude && (
                    <span className="text-xs text-orange-600 font-medium bg-orange-100 px-2 py-1 rounded">📊 Order of Magnitude</span>
                  )}
                  {isPowerUpsMode && !lastResult?.isCorrect && (
                    <span className="text-xs text-red-600 font-medium bg-red-100 px-2 py-1 rounded">
                      💔 Lost a life! {currentLives} remaining
                    </span>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>

      {/* Fun Facts */}
      {currentQuestion.targetObject.facts && currentQuestion.targetObject.facts.length > 0 && (
        <div className="card bg-orange-50">
          <h4 className="text-lg font-bold text-orange-800 mb-2">
            🤓 Fun Fact about {currentQuestion.targetObject.displayName}:
          </h4>
          <p className="text-orange-700">
            {currentQuestion.targetObject.facts[Math.floor(Math.random() * currentQuestion.targetObject.facts.length)]}
          </p>
        </div>
      )}
    </div>
  );
};

export default GameScreen; 